import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { ChromeStatus } from '@urc-ui/utils/chrome-utils';
import { logger } from '@urc-ui/services/logger';
import { ChromeStatusContext } from '@urc-ui/context/aac-chrome-status';
import { OutageBanner, PreOutageBanner } from '@urc-ui/components/outage-banner';

const CHROME_COMPONENTS_INITIALIZED = 'CHROME_COMPONENTS_INITIALIZED';

/**
 * The console team fires a custom event (CHROME_COMPONENTS_INITIALIZED) when they have initialized all the global
 * nav components and chrome API framework. See CCX CR https://code.amazon.com/reviews/CR-91935273/revisions/2#/details.
 * In our app, we block rendering until their component is fully loaded. We also want to gracefully degrade if their
 * app fails to load after 2s, hence the FORCED_READY state.
 */
const Layout = () => {
  const [start] = useState(Date.now());
  const [chromeStatus, setChromeStatus] = useState(window.AACChrome?.hasInitialized ? ChromeStatus.READY : ChromeStatus.NOT_READY);

  useEffect(() => {
    const chromeListener = () => {
      if (window.AACChrome?.hasInitialized) {
        const end = Date.now();
        logger.logInfoMessage(`Chrome loaded after ${(end - start) / 1000} seconds`);
        setChromeStatus(ChromeStatus.READY);
      }
    };

    window.addEventListener(CHROME_COMPONENTS_INITIALIZED, chromeListener);
    return () => window.removeEventListener(CHROME_COMPONENTS_INITIALIZED, chromeListener);
  }, [chromeStatus, start]);

  useEffect(() => {
    if (chromeStatus !== ChromeStatus.NOT_READY) {
      return;
    }

    const beacon = setTimeout(() => {
      setChromeStatus(ChromeStatus.FORCED_READY);
      logger.logErrorMessage('Failed to load Chrome after 2s');
    }, 2000);

    return () => clearTimeout(beacon);
  }, [chromeStatus]);

  if (window.AppContext?.isOutageEnabled === 'true') {
    return <OutageBanner end={window.AppContext.outageEndTime as string} />;
  }

  const isChromeReady = chromeStatus === ChromeStatus.FORCED_READY || chromeStatus === ChromeStatus.READY;
  return (
    <div id='application-container' data-testid='application-container'>
      <ChromeStatusContext.Provider value={isChromeReady}>
        {window.AppContext?.isPreOutageEnabled === 'true' ? (
          <PreOutageBanner start={window.AppContext.outageStartTime as string} end={window.AppContext.outageEndTime as string} />
        ) : null}
        <Outlet />
      </ChromeStatusContext.Provider>
    </div>
  );
};

export { Layout };
